import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { Switch } from "../ui/switch";
import { toast } from "../../hooks/use-toast";
import { Mail, X, Plus, FileText, Table2 } from "lucide-react";
import apiService from "../../services/apiService";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  customerId: string;
}

const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export default function InvoiceDashboardEmailSettings({ open, onOpenChange, customerId }: Props) {
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [currentToEmail, setCurrentToEmail] = useState("");
  const [currentCcEmail, setCurrentCcEmail] = useState("");
  const [toError, setToError] = useState("");
  const [ccError, setCcError] = useState("");
  const [invoiceFormat, setInvoiceFormat] = useState<"per-invoice" | "monthly-summary">("per-invoice");
  const [eInvoicingEnabled, setEInvoicingEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const persistEmailSettings = async (nextTo: string[], nextCc: string[]) => {
    if (!customerId) return;
    setSaving(true);
    try {
      const payload = {
        toEmails: nextTo,
        ccEmails: nextCc,
        invoiceFormat,
        eInvoicingEnabled,
      };
      await apiService.put(`/customers/rental-invoice-email-settings/${customerId}`, payload as any);
      toast({
        title: "Email Settings Saved",
        description: "Settings updated successfully.",
      });
    } catch (e) {
      toast({
        title: "Save failed",
        description: "Could not update settings. Please try again.",
        variant: "destructive",
      });
      throw e;
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    let ignore = false;
    const load = async () => {
      if (!open) return;
      if (!customerId) return;
      setLoading(true);
      try {
        const resp = await apiService.get<any>(`/customers/rental-invoice-email-settings/${customerId}`);
        const payload = resp?.data ?? resp;
        const nextTo = Array.isArray(payload?.toEmails)
          ? payload.toEmails
          : Array.isArray(payload?.reminderToEmails)
            ? payload.reminderToEmails
            : [];
        const nextCc = Array.isArray(payload?.ccEmails)
          ? payload.ccEmails
          : Array.isArray(payload?.reminderCcEmails)
            ? payload.reminderCcEmails
            : [];
        const nextFormat = payload?.invoiceFormat ?? payload?.format;
        const nextEInvoicing =
          payload?.eInvoicingEnabled ??
          payload?.enableEInvoicing ??
          payload?.isEInvoicingEnabled ??
          false;
        if (!ignore) {
          setToEmails(nextTo.filter((x: any) => typeof x === "string"));
          setCcEmails(nextCc.filter((x: any) => typeof x === "string"));
          if (nextFormat === "per-invoice" || nextFormat === "monthly-summary") setInvoiceFormat(nextFormat);
          setEInvoicingEnabled(Boolean(nextEInvoicing));
        }
      } catch (e) {
        if (!ignore) {
          toast({
            title: "Load failed",
            description: "Could not load email settings.",
            variant: "destructive",
          });
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    };
    void load();
    return () => { ignore = true; };
  }, [open, customerId]);

  const handleAddEmail = (
    value: string,
    which: "to" | "cc",
  ) => {
    const email = value.trim().toLowerCase();
    if (!email) return;
    if (!isValidEmail(email)) {
      if (which === "to") setToError("Please enter a valid email address");
      else setCcError("Please enter a valid email address");
      return;
    }
    const currentList = which === "to" ? toEmails : ccEmails;
    const otherList = which === "to" ? ccEmails : toEmails;
    if (currentList.includes(email) || otherList.includes(email)) {
      if (which === "to") setToError("This email has already been added");
      else setCcError("This email has already been added");
      return;
    }
    const nextTo = which === "to" ? [...toEmails, email] : toEmails;
    const nextCc = which === "cc" ? [...ccEmails, email] : ccEmails;

    // Update local state only. Persist happens on "Save Settings".
    if (which === "to") setToEmails(nextTo);
    else setCcEmails(nextCc);
    if (which === "to") {
      setCurrentToEmail("");
      setToError("");
    } else {
      setCurrentCcEmail("");
      setCcError("");
    }
  };

  const handleToKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail(currentToEmail, "to");
    }
  };

  const handleCcKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmail(currentCcEmail, "cc");
    }
  };

  const handleSave = () => {
    void persistEmailSettings(toEmails, ccEmails)
      .then(() => onOpenChange(false))
      .catch(() => {});
  };

  const handleClose = () => {
    onOpenChange(false);
    setToError("");
    setCcError("");
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5 text-primary" />
            Email Settings
          </DialogTitle>
          <DialogDescription>
            Configure email recipients and invoice delivery format
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Enable E-Invoicing */}
          <div className="flex items-center justify-between gap-4 rounded-lg border p-4">
            <div className="min-w-0 space-y-0.5">
              <Label htmlFor="e-invoicing-toggle" className="text-sm font-semibold cursor-pointer">
                Enable E-Invoicing
              </Label>
              {/* <p className="text-xs text-muted-foreground leading-relaxed">
                Mandatory once PAN-level turnover crosses ₹5Cr. Skips B2C / unregistered buyers automatically.
              </p> */}
            </div>
            <Switch
              id="e-invoicing-toggle"
              checked={eInvoicingEnabled}
              onCheckedChange={setEInvoicingEnabled}
              disabled={loading || saving}
              className="shrink-0 data-[state=unchecked]:border-muted-foreground/40"
            />
          </div>

          {/* Invoice Format Toggle */}
          <div className="space-y-3">
            <Label className="text-sm font-medium">Invoice Format</Label>
            <RadioGroup value={invoiceFormat} onValueChange={(v) => setInvoiceFormat(v as "per-invoice" | "monthly-summary")} className="grid grid-cols-2 gap-3">
              <Label
                htmlFor="per-invoice"
                className={`flex items-center gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${
                  invoiceFormat === "per-invoice" ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}
              >
                <RadioGroupItem value="per-invoice" id="per-invoice" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <FileText className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Per-Invoice</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">Send individual invoice PDF per schedule per period</p>
                </div>
              </Label>
              <Label
                htmlFor="monthly-summary"
                className={`flex items-center gap-3 rounded-lg border-2 p-3 cursor-pointer transition-colors ${
                  invoiceFormat === "monthly-summary" ? "border-primary bg-primary/5" : "border-border hover:border-muted-foreground/30"
                }`}
              >
                <RadioGroupItem value="monthly-summary" id="monthly-summary" />
                <div className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <Table2 className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium">Monthly Summary</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">Send one consolidated Excel report for all invoices in the month</p>
                </div>
              </Label>
            </RadioGroup>
          </div>

          {/* TO Emails */}
          <div className="space-y-2">
            <Label htmlFor="to-emails" className="text-sm font-medium">
              To <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="to-emails"
                type="email"
                placeholder="recipient@company.com"
                value={currentToEmail}
                onChange={(e) => { setCurrentToEmail(e.target.value); setToError(""); }}
                onKeyDown={handleToKeyDown}
                className={toError ? "border-destructive" : ""}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={() => handleAddEmail(currentToEmail, "to")}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {toError && <p className="text-xs text-destructive">{toError}</p>}
            <p className="text-xs text-muted-foreground">Press Enter or click + to add email</p>
            {toEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {toEmails.map((email) => (
                  <Badge key={email} variant="secondary" className="gap-1 pl-2 pr-1 py-1 text-xs">
                    {email}
                    <button
                      disabled={saving || loading}
                      onClick={() => {
                        const next = toEmails.filter((e) => e !== email);
                        setToEmails(next);
                      }}
                      className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* CC Emails */}
          <div className="space-y-2">
            <Label htmlFor="cc-emails" className="text-sm font-medium">
              CC <span className="text-muted-foreground text-xs font-normal">(Optional)</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="cc-emails"
                type="email"
                placeholder="cc@company.com"
                value={currentCcEmail}
                onChange={(e) => { setCurrentCcEmail(e.target.value); setCcError(""); }}
                onKeyDown={handleCcKeyDown}
                className={ccError ? "border-destructive" : ""}
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="shrink-0"
                onClick={() => handleAddEmail(currentCcEmail, "cc")}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {ccError && <p className="text-xs text-destructive">{ccError}</p>}
            <p className="text-xs text-muted-foreground">Press Enter or click + to add email</p>
            {ccEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-1">
                {ccEmails.map((email) => (
                  <Badge key={email} variant="secondary" className="gap-1 pl-2 pr-1 py-1 text-xs">
                    {email}
                    <button
                      disabled={saving || loading}
                      onClick={() => {
                        const next = ccEmails.filter((e) => e !== email);
                        setCcEmails(next);
                      }}
                      className="ml-0.5 rounded-full hover:bg-muted-foreground/20 p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={saving || loading}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
